import cn from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useRef } from 'react';
import { useClickAway, useToggle } from 'react-use';

import { __t } from '@mssgme/shared';
import { isMatchId } from '@mssgme/helpers';
import { ChevronDownIcon } from '@mssgme/icons';
import { Fade, TextButton } from '@mssgme/ui';

import styles from './FilterMenu.scss';

export const FilterMenuItem = ({ collection, active, onSelect }) => {
    const handleClick = useCallback(() => onSelect(collection._id), [onSelect, collection]);

    return (
        <div className={cn(styles.option, active && styles.active)} onClick={handleClick}>
            <span className={styles.optionLabel}>{collection.default ? __t('all_products') : collection.name}</span>
            <span className={styles.badge}>{collection.publishedProductsCount}</span>
        </div>
    );
};

FilterMenuItem.propTypes = {
    collection: PropTypes.object.isRequired,
    onSelect: PropTypes.func.isRequired,
    active: PropTypes.bool,
};

export const FilterMenu = ({ items, activeCollectionId, theme, interactive, onSelect, ...rest }) => {
    const root = useRef();
    const [isOpen, toggle] = useToggle();
    const selected = items.find(isMatchId(activeCollectionId));

    useClickAway(root, () => toggle(false));

    const handleSelect = useCallback((id) => {
        onSelect(id);
        toggle(false);
    });

    return (
        <div className={styles.root} {...rest}>
            <div className={styles.select} ref={root}>
                <TextButton aria-pressed={isOpen} className={styles.button} onClick={toggle}>
                    <span className={styles.label}>
                        {selected && (selected.default ? __t('all_products') : selected.name)}
                        {selected && <span>&nbsp;({selected?.publishedProductsCount})</span>}
                    </span>
                    <ChevronDownIcon size={16} className={styles.arrow} />
                </TextButton>

                <Fade in={isOpen}>
                    <div className={styles.dropdown}>
                        {items.map((collection) => (
                            <FilterMenuItem
                                key={collection._id}
                                active={collection._id === activeCollectionId}
                                collection={collection}
                                onSelect={handleSelect}
                            />
                        ))}
                    </div>
                </Fade>
            </div>
        </div>
    );
};

FilterMenu.propTypes = {
    items: PropTypes.array.isRequired,
    baseUrl: PropTypes.string.isRequired,
    buttonStyle: PropTypes.object,
    arrowsStyle: PropTypes.object,
    interactive: PropTypes.bool,
    activeCollectionId: PropTypes.string,
    theme: PropTypes.object.isRequired,
    onSelect: PropTypes.func,
};
